import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { db } from '../lib/supabase'
import LoadingSpinner from '../components/LoadingSpinner'

export default function Home() {
  const [activeFilter, setActiveFilter] = useState(null)
  const [marcas, setMarcas] = useState([])
  const filtersRef = useRef(null)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [homeEventos, setHomeEventos] = useState([])
  const [heroConfig, setHeroConfig] = useState(null)
  const [homeCards, setHomeCards] = useState([])
  const [homeBlock, setHomeBlock] = useState(null)
  const [seoConfig, setSeoConfig] = useState(null)
  const [siteConfig, setSiteConfig] = useState(null)
  const [faqs, setFaqs] = useState([])
  const [filters, setFilters] = useState([])
  const [allCategories, setAllCategories] = useState([])

  useEffect(() => {
    loadMarcas()
    loadHomeEventos()
    loadHeroConfig()
    loadHomeCards()
    loadHomeBlock()
    loadSEOConfig()
    loadSiteConfig()
    loadFAQs()
  }, [])

  useEffect(() => {
    if (marcas.length > 0 && heroConfig !== null) {
      loadFilters()
    }
  }, [marcas, heroConfig])

  const loadMarcas = async () => {
    const { data } = await db.getTiendas()
    if (data) setMarcas(data)
  }

  const loadHomeEventos = async () => {
    const { data } = await db.getEventosHome()
    setHomeEventos(data || [])
  }

  const loadHeroConfig = async () => {
    const { data } = await db.getConfiguracionPortada('home')
    setHeroConfig(data || null)
  }

  const loadHomeCards = async () => {
    const { data } = await db.getHomeCards()
    setHomeCards(data || [])
  }

  const loadHomeBlock = async () => {
    const { data } = await db.getHomeBlock()
    setHomeBlock(data || null)
  }

  const loadSEOConfig = async () => {
    const { data } = await db.getSEOConfig('home')
    setSeoConfig(data || null)
  }

  const loadSiteConfig = async () => {
    const { data } = await db.getSiteConfig()
    setSiteConfig(data || null)
  }

  const loadFAQs = async () => {
    const { data } = await db.getFAQsByScope('page', null, 'home')
    setFaqs(data || [])
  }

  const loadFilters = async () => {
    // Get all categories for counting
    const { data: categories } = await db.getCategorias('tiendas')
    setAllCategories(categories || [])

    // Count stores per category
    const categoryCounts = {}
    marcas.forEach(marca => {
      if (marca.categoria?.nombre) {
        categoryCounts[marca.categoria.nombre] = (categoryCounts[marca.categoria.nombre] || 0) + 1
      }
    })

    // If heroConfig has brand_filters configured, use those
    if (heroConfig?.brand_filters && Array.isArray(heroConfig.brand_filters) && heroConfig.brand_filters.length > 0) {
      // Filter out any configured filters that have no stores
      const validFilters = heroConfig.brand_filters
        .filter(filter => categoryCounts[filter.nombre] > 0)
        .map(filter => filter.nombre)
      
      setFilters(validFilters.length > 0 ? validFilters : getTopFilters(categoryCounts))
    } else {
      // Fallback: auto-pick top 5 categories by count
      setFilters(getTopFilters(categoryCounts))
    }
  }

  const getTopFilters = (categoryCounts) => {
    return Object.entries(categoryCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name]) => name)
  }

  // Filter brands by active category
  const filteredMarcas = activeFilter === 'Todos' || !activeFilter
    ? marcas
    : marcas.filter(marca => marca.categoria?.nombre === activeFilter)

  // Show 4 brands on desktop, 2 on mobile
  const brandsToShow = window.innerWidth < 600 ? 2 : 4
  const maxSlide = Math.max(0, filteredMarcas.length - brandsToShow)

  // Only show brands sections if we have at least one store
  const hasTiendas = marcas.length > 0

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev >= maxSlide ? 0 : prev + 1))
  }

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev <= 0 ? maxSlide : prev - 1))
  }

  // Reset slide position when filter changes
  useEffect(() => {
    setCurrentSlide(0)
  }, [activeFilter])

  // Click-away to clear filter
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filtersRef.current && !filtersRef.current.contains(event.target)) {
        setActiveFilter(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <>
      <Helmet>
        <title>{seoConfig?.meta_title || 'Plaza Aurora - Tu Outlet Favorito'}</title>
        <meta name="description" content={seoConfig?.meta_description || 'Donde las mejores marcas se encuentran con los mejores precios'} />
        <link rel="canonical" href={seoConfig?.canonical_url || `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/`} />
        
        {/* Preload LCP Hero Image */}
        {heroConfig?.hero_imagen_url && (
          <link rel="preload" as="image" href={heroConfig.hero_imagen_url} />
        )}
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="es_PE" />
        <meta property="og:site_name" content="Plaza Aurora" />
        <meta property="og:url" content={seoConfig?.canonical_url || `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/`} />
        <meta property="og:title" content={seoConfig?.og_title || seoConfig?.meta_title || 'Plaza Aurora - Tu Outlet Favorito'} />
        <meta property="og:description" content={seoConfig?.og_description || seoConfig?.meta_description || 'Donde las mejores marcas se encuentran con los mejores precios'} />
        {seoConfig?.og_image_url && <meta property="og:image" content={seoConfig.og_image_url} />}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content={seoConfig?.twitter_card || 'summary_large_image'} />
        <meta name="twitter:title" content={seoConfig?.og_title || seoConfig?.meta_title || 'Plaza Aurora - Tu Outlet Favorito'} />
        <meta name="twitter:description" content={seoConfig?.og_description || seoConfig?.meta_description || 'Donde las mejores marcas se encuentran con los mejores precios'} />
        {seoConfig?.og_image_url && <meta name="twitter:image" content={seoConfig.og_image_url} />}
        
        {/* Robots */}
        <meta name="robots" content={seoConfig?.robots_index === false ? 'noindex,nofollow' : 'index,follow'} />
        
        {/* LLM Discovery - JSON Feeds */}
        <link rel="alternate" type="application/json" href={`${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/api/site.json`} title="Site Information" />
        <link rel="alternate" type="application/json" href={`${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/api/brands.json`} title="Brands List" />
        <link rel="alternate" type="application/json" href={`${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/api/events.json`} title="Events List" />
        <link rel="alternate" type="application/json" href={`${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/kg.json`} title="Knowledge Graph" />
      </Helmet>
      
      {seoConfig?.json_ld && (
        <script type="application/ld+json">
          {seoConfig.json_ld}
        </script>
      )}

      {/* Organization JSON-LD */}
      {siteConfig && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ShoppingCenter",
            "name": siteConfig.org_name || "Plaza Aurora",
            "description": "Centro comercial outlet en Lima Norte con las mejores marcas y precios. Ubicado en Independencia, atendemos a Los Olivos, San Mart\u00edn de Porres, Comas y toda Lima Norte.",
            "url": siteConfig.site_url || "https://www.plazaaurora.pe",
            "logo": siteConfig.logo_url || "https://ffbzkwlpbswgbngwvyvp.supabase.co/storage/v1/object/public/assets/logo/PlazaAurora_Logo.png",
            "image": siteConfig.logo_url || "https://ffbzkwlpbswgbngwvyvp.supabase.co/storage/v1/object/public/assets/logo/PlazaAurora_Logo.png",
            "telephone": siteConfig.contact_phone || "",
            "email": siteConfig.contact_email || "comercial@plazaaurora.pe",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": siteConfig.address_street || "Av. Carlos Izaguirre 271-275",
              "addressLocality": siteConfig.address_locality || "Independencia",
              "addressRegion": siteConfig.address_region || "Lima",
              "postalCode": siteConfig.postal_code || "15113",
              "addressCountry": siteConfig.address_country || "PE"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": siteConfig.latitude || -11.990286,
              "longitude": siteConfig.longitude || -77.062296
            },
            "openingHoursSpecification": siteConfig.opening_hours_json || [
              {"@type":"OpeningHoursSpecification","dayOfWeek":"Monday","opens":"08:00","closes":"22:00"},
              {"@type":"OpeningHoursSpecification","dayOfWeek":"Tuesday","opens":"08:00","closes":"22:00"},
              {"@type":"OpeningHoursSpecification","dayOfWeek":"Wednesday","opens":"08:00","closes":"22:00"},
              {"@type":"OpeningHoursSpecification","dayOfWeek":"Thursday","opens":"08:00","closes":"22:00"},
              {"@type":"OpeningHoursSpecification","dayOfWeek":"Friday","opens":"08:00","closes":"22:00"},
              {"@type":"OpeningHoursSpecification","dayOfWeek":"Saturday","opens":"08:00","closes":"22:00"},
              {"@type":"OpeningHoursSpecification","dayOfWeek":"Sunday","opens":"08:00","closes":"22:00"}
            ],
            "sameAs": siteConfig.same_as || [
              "https://www.facebook.com/profile.php?id=61582145587744",
              "https://www.instagram.com/ccplazaaurora/",
              "https://www.tiktok.com/@ccplazaaurora"
            ],
            "priceRange": "$$",
            "currenciesAccepted": "PEN",
            "paymentAccepted": "Cash, Credit Card, Debit Card",
            "areaServed": [
              {
                "@type": "City",
                "name": "Independencia",
                "containedInPlace": {"@type": "City", "name": "Lima"}
              },
              {
                "@type": "City",
                "name": "Los Olivos",
                "containedInPlace": {"@type": "City", "name": "Lima"}
              },
              {
                "@type": "City",
                "name": "San Mart\u00edn de Porres",
                "containedInPlace": {"@type": "City", "name": "Lima"}
              },
              {
                "@type": "City",
                "name": "Comas",
                "containedInPlace": {"@type": "City", "name": "Lima"}
              },
              {
                "@type": "Place",
                "name": "Lima Norte"
              }
            ],
            "keywords": "outlet Lima Norte, tiendas Independencia, mejores ofertas Lima Norte, promociones Los Olivos, tiendas San Mart\u00edn de Porres, descuentos Comas, centro comercial Independencia, outlet Peru, mejores precios Lima, marcas reconocidas Lima Norte",
            ...(siteConfig.google_maps_url && { "hasMap": siteConfig.google_maps_url })
          })}
        </script>
      )}

      {/* WebSite with SearchAction JSON-LD */}
      {siteConfig && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": siteConfig.org_name || "Plaza Aurora",
            "url": siteConfig.site_url || "https://www.plazaaurora.pe",
            "potentialAction": {
              "@type": "SearchAction",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": `${siteConfig.site_url || 'https://www.plazaaurora.pe'}/marcas?q={search_term_string}`
              },
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
      )}

      {/* SiteNavigationElement JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "itemListElement": [
            {
              "@type": "SiteNavigationElement",
              "position": 1,
              "name": "Inicio",
              "url": `${siteConfig?.site_url || 'https://www.plazaaurora.pe'}/`
            },
            {
              "@type": "SiteNavigationElement",
              "position": 2,
              "name": "Marcas",
              "description": "Descubre todas las marcas disponibles en Plaza Aurora",
              "url": `${siteConfig?.site_url || 'https://www.plazaaurora.pe'}/marcas`
            },
            {
              "@type": "SiteNavigationElement",
              "position": 3,
              "name": "Eventos",
              "description": "Conoce los próximos eventos y promociones",
              "url": `${siteConfig?.site_url || 'https://www.plazaaurora.pe'}/eventos`
            },
            {
              "@type": "SiteNavigationElement",
              "position": 4,
              "name": "Ubícanos",
              "description": "Encuentra nuestra ubicación y horarios",
              "url": `${siteConfig?.site_url || 'https://www.plazaaurora.pe'}/ubicanos`
            },
            {
              "@type": "SiteNavigationElement",
              "position": 5,
              "name": "Contacto",
              "description": "Contacta con nosotros para formar parte de Plaza Aurora",
              "url": `${siteConfig?.site_url || 'https://www.plazaaurora.pe'}/contacto`
            },
            {
              "@type": "SiteNavigationElement",
              "position": 6,
              "name": "Trabaja con Nosotros",
              "description": "Únete a nuestro equipo",
              "url": `${siteConfig?.site_url || 'https://www.plazaaurora.pe'}/trabaja`
            },
            {
              "@type": "SiteNavigationElement",
              "position": 7,
              "name": "Legal",
              "description": "Políticas de privacidad y términos legales",
              "url": `${siteConfig?.site_url || 'https://www.plazaaurora.pe'}/legal`
            }
          ]
        })}
      </script>

      <main>
        <div className="container" style={{ maxWidth: 'var(--container-max-width)', margin: '0 auto', padding: '0 2rem' }}>
          {/* Hero Section */}
          <section id="hero_main" role="region" aria-label="Hero - Reflect Fashion" style={{ marginTop: '1.5rem' }}>
            <div className="heroContainer" style={{
              display: 'grid',
              gridTemplateColumns: '1fr 0.9fr',
              alignItems: 'center',
              gap: '1.5rem',
              padding: '0 1rem',
              background: 'var(--color-accent-gray)',
              borderRadius: 'var(--radius-lg)',
              overflow: 'hidden'
            }}>
              <div className="heroLeft" style={{ padding: '1rem' }}>
                <h1 className="heroTitle" style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 'var(--font-weight-bold)',
                  lineHeight: '0.95',
                  color: 'var(--color-text-primary)',
                  margin: '0 0 1rem 0'
                }}>
                  {heroConfig?.hero_titulo || 'LOS MEJORES OUTLETS DE LIMA NORTE'}<br/>
                </h1>
                <p className="heroDescription" style={{
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-lead)',
                  color: 'var(--color-text-muted)',
                  maxWidth: '48ch',
                  lineHeight: 'var(--line-height-relaxed)',
                  marginBottom: '1.5rem'
                }}>
                  {heroConfig?.hero_descripcion || 'Donde las mejores marcas se encuentran con los mejores precios 🛍️'}
                </p>
                <div className="heroActions" style={{ display: 'flex', gap: '0.75rem' }}>
                  <Link 
                    to={heroConfig?.hero_boton_1_url || '/marcas'} 
                    className={heroConfig?.hero_boton_1_estilo === 'outline' ? 'btnOutline' : 'btnPrimary'} 
                    style={{
                      padding: '0.6rem 1.1rem',
                      background: 'var(--color-cta-bg)',
                      color: 'var(--color-cta-text)',
                      borderRadius: 'var(--radius-sm)',
                      fontWeight: 'var(--font-weight-bold)',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease, color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
                      fontSize: 'var(--font-size-body)',
                      textDecoration: 'none',
                      display: 'inline-block'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)'
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                      e.currentTarget.style.background = 'var(--brand-blue)'
                      e.currentTarget.style.color = 'white'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = 'none'
                      e.currentTarget.style.background = 'var(--color-cta-bg)'
                      e.currentTarget.style.color = 'var(--color-cta-text)'
                    }}
                    onMouseDown={(e) => {
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-400)'
                      e.currentTarget.style.background = 'var(--brand-blue-600)'
                    }}
                    onTouchStart={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)'
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                      e.currentTarget.style.background = 'var(--brand-blue)'
                      e.currentTarget.style.color = 'white'
                    }}
                    onTouchEnd={(e) => {
                      setTimeout(() => {
                        e.currentTarget.style.transform = 'translateY(0)'
                        e.currentTarget.style.boxShadow = 'none'
                        e.currentTarget.style.background = 'var(--color-cta-bg)'
                        e.currentTarget.style.color = 'var(--color-cta-text)'
                      }, 150)
                    }}
                  >
                    {heroConfig?.hero_boton_1_texto || 'Descubre Descuentos'}
                  </Link>
                  <Link to={heroConfig?.hero_boton_2_url || '/eventos'} className={heroConfig?.hero_boton_2_estilo === 'primary' ? 'btnPrimary' : 'btnOutline'} style={{
                    padding: '0.6rem 1.1rem',
                    background: 'transparent',
                    border: '1px solid var(--color-stroke)',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease, background-color 0.2s ease',
                    fontSize: 'var(--font-size-body)',
                    color: 'var(--color-text-primary)',
                    textDecoration: 'none',
                    display: 'inline-block'
                  }}>
                    {heroConfig?.hero_boton_2_texto || 'Explora Eventos'}
                  </Link>
                </div>
              </div>
              <div className="heroRight" style={{ height: '100%' }}>
                {heroConfig?.hero_imagen_url ? (
                  <img 
                    className="heroImage" 
                    src={heroConfig.hero_imagen_url} 
                    alt={heroConfig?.hero_imagen_alt || 'Plaza Aurora'} 
                    style={{
                      width: '100%',
                      height: '420px',
                      objectFit: 'cover',
                      borderRadius: 'var(--radius-md)'
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: '100%',
                      height: '420px',
                      borderRadius: 'var(--radius-md)',
                      background: 'var(--color-accent-gray)'
                    }}
                  />
                )}
              </div>
            </div>
          </section>
        </div>

        {/* Promo Strip - Full Width */}
        <section id="promo_strip" role="complementary" aria-label="Promo marquee" style={{ 
          marginTop: '1.5rem', 
          overflow: 'hidden', 
          display: heroConfig?.marquee_activo === false ? 'none' : 'block'
        }}>
          {heroConfig?.marquee_link_url
            ? (heroConfig.marquee_link_url.startsWith('/')
                ? (
                  <Link to={heroConfig.marquee_link_url} style={{ textDecoration: 'none', cursor: 'pointer' }}>
                    <div className="marquee" style={{
                      background: 'var(--color-black-block)',
                      color: 'var(--color-cta-text)',
                      fontFamily: 'var(--font-display)',
                      fontSize: '0.875rem',
                      padding: '0.5rem 0',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      transition: 'opacity 0.2s ease'
                    }}>
                      <div className="marquee-content" style={{
                        display: 'inline-block',
                        animation: 'scroll-left 25s linear infinite',
                        paddingLeft: '100%'
                      }}>
                        {heroConfig?.marquee_texto || '★ LAS MEJORES MARCAS  •  LOS MEJORES PRECIOS  •  LAS MEJORES MARCAS  •  LOS MEJORES PRECIOS  •  LAS MEJORES MARCAS  •  LOS MEJORES PRECIOS  •  LAS MEJORES MARCAS  •  LOS MEJORES PRECIOS'}
                      </div>
                    </div>
                  </Link>
                ) : (
                  <a href={heroConfig.marquee_link_url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', cursor: 'pointer' }}>
                    <div className="marquee" style={{
                      background: 'var(--color-black-block)',
                      color: 'var(--color-cta-text)',
                      fontFamily: 'var(--font-display)',
                      fontSize: '0.875rem',
                      padding: '0.5rem 0',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      transition: 'opacity 0.2s ease'
                    }}>
                      <div className="marquee-content" style={{
                        display: 'inline-block',
                        animation: 'scroll-left 25s linear infinite',
                        paddingLeft: '100%'
                      }}>
                        {heroConfig?.marquee_texto || '★ LAS MEJORES MARCAS  •  LOS MEJORES PRECIOS  •  LAS MEJORES MARCAS  •  LOS MEJORES PRECIOS  •  LAS MEJORES MARCAS  •  LOS MEJORES PRECIOS  •  LAS MEJORES MARCAS  •  LOS MEJORES PRECIOS'}
                      </div>
                    </div>
                  </a>
                )
              )
            : (
              <div className="marquee" style={{
                background: 'var(--color-black-block)',
                color: 'var(--color-cta-text)',
                fontFamily: 'var(--font-display)',
                fontSize: '0.875rem',
                padding: '0.5rem 0',
                overflow: 'hidden',
                whiteSpace: 'nowrap'
              }}>
                <div className="marquee-content" style={{
                  display: 'inline-block',
                  animation: 'scroll-left 25s linear infinite',
                  paddingLeft: '100%'
                }}>
                  {heroConfig?.marquee_texto || '★ LAS MEJORES MARCAS  •  LOS MEJORES PRECIOS  •  LAS MEJORES MARCAS  •  LOS MEJORES PRECIOS  •  LAS MEJORES MARCAS  •  LOS MEJORES PRECIOS  •  LAS MEJORES MARCAS  •  LOS MEJORES PRECIOS'}
                </div>
              </div>
            )
          }
        </section>

        <div className="container" style={{ maxWidth: 'var(--container-max-width)', margin: '0 auto', padding: '0 2rem' }}>
          {/* Featured Products Top Section */}
          <section className="featured-products-top" style={{ marginTop: '1.5rem' }}>
            <div className="featured-grid-top" style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: '1rem'
            }}>
              {(() => {
                const cardTiendas = homeCards.find(c => c.tipo === 'tiendas' && c.activo)
                if (!cardTiendas) {
                  return (
                    <div className="featured-card" style={{ position: 'relative', borderRadius: 'var(--radius-md)', overflow: 'hidden', background: 'var(--color-accent-gray)' }}>
                      <div style={{ width: '100%', height: '340px', background: 'var(--color-accent-gray)' }} />
                      <div className="featured-info" style={{ position: 'absolute', bottom: '1rem', left: '1rem', background: 'white', padding: '0.5rem 1rem', borderRadius: 'var(--radius-sm)' }}>
                        <h3 style={{ fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-body)', fontWeight: 'var(--font-weight-bold)', margin: 0, color: 'var(--color-text-primary)' }}>Explora tiendas</h3>
                      </div>
                    </div>
                  )
                }
                return cardTiendas.enlace_url?.startsWith('/')
                  ? (
                    <Link to={cardTiendas.enlace_url} className="featured-card" style={{ position: 'relative', borderRadius: 'var(--radius-md)', overflow: 'hidden', textDecoration: 'none' }}>
                      <img src={cardTiendas.imagen_url} alt={cardTiendas.alt_text || cardTiendas.texto} style={{ width: '100%', height: '340px', objectFit: 'cover' }}/>
                      <div className="featured-info" style={{ position: 'absolute', bottom: '1rem', left: '1rem', background: 'white', padding: '0.5rem 1rem', borderRadius: 'var(--radius-sm)' }}>
                        <h3 style={{ fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-body)', fontWeight: 'var(--font-weight-bold)', margin: 0, color: 'var(--color-text-primary)' }}>{cardTiendas.texto}</h3>
                      </div>
                    </Link>
                  ) : (
                    <a href={cardTiendas.enlace_url || '#'} target="_blank" rel="noopener noreferrer" className="featured-card" style={{ position: 'relative', borderRadius: 'var(--radius-md)', overflow: 'hidden', textDecoration: 'none' }}>
                      <img src={cardTiendas.imagen_url} alt={cardTiendas.alt_text || cardTiendas.texto} style={{ width: '100%', height: '340px', objectFit: 'cover' }}/>
                      <div className="featured-info" style={{ position: 'absolute', bottom: '1rem', left: '1rem', background: 'white', padding: '0.5rem 1rem', borderRadius: 'var(--radius-sm)' }}>
                        <h3 style={{ fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-body)', fontWeight: 'var(--font-weight-bold)', margin: 0, color: 'var(--color-text-primary)' }}>{cardTiendas.texto}</h3>
                      </div>
                    </a>
                  )
              })()}
              
              {(() => {
                const cardEventos = homeCards.find(c => c.tipo === 'eventos' && c.activo)
                if (!cardEventos) {
                  return (
                    <div className="featured-card" style={{ position: 'relative', borderRadius: 'var(--radius-md)', overflow: 'hidden', background: 'var(--color-accent-gray)' }}>
                      <div style={{ width: '100%', height: '340px', background: 'var(--color-accent-gray)' }} />
                      <div className="featured-info" style={{ position: 'absolute', bottom: '1rem', left: '1rem', background: 'white', padding: '0.5rem 1rem', borderRadius: 'var(--radius-sm)' }}>
                        <h3 style={{ fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-body)', fontWeight: 'var(--font-weight-bold)', margin: 0, color: 'var(--color-text-primary)' }}>Explora eventos</h3>
                      </div>
                    </div>
                  )
                }
                return cardEventos.enlace_url?.startsWith('/')
                  ? (
                    <Link to={cardEventos.enlace_url} className="featured-card" style={{ position: 'relative', borderRadius: 'var(--radius-md)', overflow: 'hidden', textDecoration: 'none' }}>
                      <img src={cardEventos.imagen_url} alt={cardEventos.alt_text || cardEventos.texto} style={{ width: '100%', height: '340px', objectFit: 'cover' }}/>
                      <div className="featured-info" style={{ position: 'absolute', bottom: '1rem', left: '1rem', background: 'white', padding: '0.5rem 1rem', borderRadius: 'var(--radius-sm)' }}>
                        <h3 style={{ fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-body)', fontWeight: 'var(--font-weight-bold)', margin: 0, color: 'var(--color-text-primary)' }}>{cardEventos.texto}</h3>
                      </div>
                    </Link>
                  ) : (
                    <a href={cardEventos.enlace_url || '#'} target="_blank" rel="noopener noreferrer" className="featured-card" style={{ position: 'relative', borderRadius: 'var(--radius-md)', overflow: 'hidden', textDecoration: 'none' }}>
                      <img src={cardEventos.imagen_url} alt={cardEventos.alt_text || cardEventos.texto} style={{ width: '100%', height: '340px', objectFit: 'cover' }}/>
                      <div className="featured-info" style={{ position: 'absolute', bottom: '1rem', left: '1rem', background: 'white', padding: '0.5rem 1rem', borderRadius: 'var(--radius-sm)' }}>
                        <h3 style={{ fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-body)', fontWeight: 'var(--font-weight-bold)', margin: 0, color: 'var(--color-text-primary)' }}>{cardEventos.texto}</h3>
                      </div>
                    </a>
                  )
              })()}
              
              <div className="featured-text-cards" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem'
              }}>
                <article className="text-card" style={{
                  background: 'var(--color-accent-gray)',
                  borderRadius: 'var(--radius-md)',
                  padding: '1.25rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '1rem',
                  flex: 1
                }}>
                  <div className="text-card-content" style={{ flex: 1 }}>
                    <p className="text-card-label" style={{
                      fontSize: 'var(--font-size-micro)',
                      fontWeight: 'var(--font-weight-medium)',
                      letterSpacing: '0.08em',
                      color: 'var(--color-text-muted)',
                      marginBottom: '0.5rem'
                    }}>{homeEventos[0]?.categoria?.nombre?.toUpperCase() || 'EVENTO DESTACADO'}</p>
                    <h3 className="text-card-title" style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-h3)',
                      fontWeight: 'var(--font-weight-bold)',
                      margin: '0 0 0.75rem 0',
                      lineHeight: 'var(--line-height-normal)',
                      color: 'var(--color-text-primary)'
                    }}>{homeEventos[0]?.nombre || 'Concurso de disfraces para niños'}</h3>
                    <Link to={homeEventos[0]?.id ? `/eventos/${homeEventos[0].id}` : '/eventos'} className="btnOutline small" style={{
                      padding: '0.4rem 0.8rem',
  fontSize: 'var(--font-size-small)',
  background: 'transparent',
  border: '1px solid var(--color-stroke)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  color: 'var(--color-text-primary)',
  textDecoration: 'none',
  display: 'inline-flex',              // CAMBIAR de inline-block a flex
  alignItems: 'center',         // AGREGAR
  lineHeight: '1'               // AGREGAR
                    }}>Inscríbete aquí</Link>
                  </div>
                  <img src={homeEventos[0]?.imagen_url || 'https://ffbzkwlpbswgbngwvyvp.supabase.co/storage/v1/object/public/assets/home/tarjetas/1761616076699_fb9831bcb1d29ee2edd8a0eb29b291fe.jpg'} alt="Evento destacado en Plaza Aurora" style={{
                    width: '80px',
                    height: '80px',
                    objectFit: 'cover',
                    borderRadius: 'var(--radius-sm)'
                  }}/>
                </article>
                
                <article className="text-card" style={{
                  background: 'var(--color-accent-gray)',
                  borderRadius: 'var(--radius-md)',
                  padding: '1.25rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '1rem',
                  flex: 1
                }}>
                  <div className="text-card-content" style={{ flex: 1 }}>
                    <p className="text-card-label" style={{
                      fontSize: 'var(--font-size-micro)',
                      fontWeight: 'var(--font-weight-medium)',
                      letterSpacing: '0.08em',
                      color: 'var(--color-text-muted)',
                      marginBottom: '0.5rem'
                    }}>{homeEventos[1]?.categoria?.nombre?.toUpperCase() || 'EVENTO GASTRONÓMICO'}</p>
                    <h3 className="text-card-title" style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-h3)',
                      fontWeight: 'var(--font-weight-bold)',
                      margin: '0 0 0.75rem 0',
                      lineHeight: 'var(--line-height-normal)',
                      color: 'var(--color-text-primary)'
                    }}>{homeEventos[1]?.nombre || 'Degustación de comida criolla'}</h3>
                    <Link to={homeEventos[1]?.id ? `/eventos/${homeEventos[1].id}` : '/eventos'} className="btnOutline small" style={{
                      fontSize: 'var(--font-size-small)',
  background: 'transparent',
  border: '1px solid var(--color-stroke)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  color: 'var(--color-text-primary)',
  textDecoration: 'none',
  display: 'inline-flex',              // CAMBIAR de inline-block a flex
  alignItems: 'center',         // AGREGAR
  lineHeight: '1'               // AGREGAR
                    }}>Inscríbete aquí</Link>
                  </div>
                  <img src={homeEventos[1]?.imagen_url || 'https://ffbzkwlpbswgbngwvyvp.supabase.co/storage/v1/object/public/assets/home/tarjetas/1761616076699_fb9831bcb1d29ee2edd8a0eb29b291fe.jpg'} alt="Evento gastronómico en Plaza Aurora" style={{
                    width: '80px',
                    height: '80px',
                    objectFit: 'cover',
                    borderRadius: 'var(--radius-sm)'
                  }}/>
                </article>
              </div>
            </div>
          </section>

          {/* Collections Intro */}
          {hasTiendas && (
          <section id="collections_intro" role="region" aria-label="Collections intro" style={{ marginTop: '2.5rem' }}>
            <div className="collection-header-wrapper" style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '2rem',
              alignItems: 'start'
            }}>
              <div className="collection-left">
                <h2 className="collection-title" style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--font-size-h1)',
                  fontWeight: 'var(--font-weight-bold)',
                  margin: '0 0 1rem 0',
                  color: 'var(--color-text-primary)'
                }}>LAS MEJORES TIENDAS</h2>
                <div ref={filtersRef} className="filters" style={{
                  display: 'flex',
                  gap: '0.5rem',
                  flexWrap: 'wrap'
                }}>
                  {filters.length > 0 ? (
                    filters.map((filter) => (
                      <button
                        key={filter}
                        className={`filter ${activeFilter === filter ? 'active' : ''}`}
                        onClick={() => setActiveFilter(filter)}
                        style={{
                          padding: '0.4rem 0.7rem',
                          borderRadius: 'var(--radius-sm)',
                          background: activeFilter === filter ? 'var(--brand-blue)' : 'var(--color-tag-bg)',
                          border: `1px solid ${activeFilter === filter ? 'var(--brand-blue)' : 'var(--color-stroke)'}`,
                          cursor: 'pointer',
                          fontSize: 'var(--font-size-small)',
                          transition: 'transform 0.2s ease, background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
                          color: activeFilter === filter ? 'white' : 'var(--color-text-primary)',
                          fontWeight: activeFilter === filter ? 'var(--font-weight-bold)' : 'var(--font-weight-regular)'
                        }}
                        onMouseEnter={(e) => {
                          if (activeFilter !== filter) {
                            e.currentTarget.style.backgroundColor = 'var(--brand-blue-50)'
                            e.currentTarget.style.borderColor = 'var(--brand-blue-300)'
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (activeFilter !== filter) {
                            e.currentTarget.style.backgroundColor = 'var(--color-tag-bg)'
                            e.currentTarget.style.borderColor = 'var(--color-stroke)'
                          }
                        }}
                        onMouseDown={(e) => {
                          e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                        }}
                        onTouchStart={(e) => {
                          if (activeFilter !== filter) {
                            e.currentTarget.style.backgroundColor = 'var(--brand-blue-50)'
                            e.currentTarget.style.borderColor = 'var(--brand-blue-300)'
                          }
                          e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                        }}
                        onTouchEnd={(e) => {
                          setTimeout(() => {
                            e.currentTarget.style.boxShadow = 'none'
                            if (activeFilter !== filter) {
                              e.currentTarget.style.backgroundColor = 'var(--color-tag-bg)'
                              e.currentTarget.style.borderColor = 'var(--color-stroke)'
                            }
                          }, 150)
                        }}
                      >
                        {filter}
                      </button>
                    ))
                  ) : (
                    <button
                      className={`filter ${activeFilter === 'Todos' ? 'active' : ''}`}
                      onClick={() => setActiveFilter('Todos')}
                      style={{
                        padding: '0.4rem 0.7rem',
                        borderRadius: 'var(--radius-sm)',
                        background: activeFilter === 'Todos' ? 'var(--brand-blue)' : 'var(--color-tag-bg)',
                        border: `1px solid ${activeFilter === 'Todos' ? 'var(--brand-blue)' : 'var(--color-stroke)'}`,
                        cursor: 'pointer',
                        fontSize: 'var(--font-size-small)',
                        transition: 'transform 0.2s ease, background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
                        color: activeFilter === 'Todos' ? 'white' : 'var(--color-text-primary)',
                        fontWeight: activeFilter === 'Todos' ? 'var(--font-weight-bold)' : 'var(--font-weight-regular)'
                      }}
                    >
                      Todos
                    </button>
                  )}
                </div>
              </div>
              <div className="collection-right">
                <p className="collection-description" style={{
                  fontSize: 'var(--font-size-body)',
                  color: 'var(--color-text-muted)',
                  lineHeight: 'var(--line-height-relaxed)'
                }}>
                  Descubre nuestra selección de las mejores tiendas a precios outlet. Desde moda deportiva hasta tecnología, encuentra todo lo que necesitas en Plaza Aurora.
                </p>
              </div>
            </div>
          </section>
          )}

          {/* Product Grid - Now Brands Slider */}
          {hasTiendas && (
          <section id="brands_slider" role="region" aria-label="Tiendas destacadas" style={{ marginTop: '1.5rem' }}>
            <div style={{ position: 'relative' }}>
              {/* Slider Container */}
              <div style={{ overflow: 'hidden' }}>
                <div className="brandsSliderTrack" style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${filteredMarcas.length}, calc(${100 / brandsToShow}% - ${(brandsToShow - 1) * 16 / brandsToShow}px))`,
                  gap: '1rem',
                  transform: `translateX(-${currentSlide * (100 / brandsToShow + 16 / brandsToShow)}%)`,
                  transition: 'transform 0.4s ease-in-out'
                }}>
                  {filteredMarcas.length > 0 ? filteredMarcas.map((marca) => (
                    <Link
                      key={marca.id}
                      to={`/marcas/${marca.id}`}
                      className="brandCard"
                      style={{
                        borderRadius: 'var(--radius-lg)',
                        overflow: 'hidden',
                        background: 'white',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease, outline 0.2s ease',
                        cursor: 'pointer',
                        textDecoration: 'none',
                        display: 'block'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)'
                        e.currentTarget.style.boxShadow = 'var(--shadow-card)'
                        e.currentTarget.style.outline = '1px solid var(--brand-blue-200)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)'
                        e.currentTarget.style.boxShadow = 'none'
                        e.currentTarget.style.outline = 'none'
                      }}
                      onTouchStart={(e) => {
                        e.currentTarget.style.transform = 'scale(0.98)'
                        e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                      }}
                      onTouchEnd={(e) => {
                        setTimeout(() => {
                          e.currentTarget.style.transform = 'translateY(0)'
                          e.currentTarget.style.boxShadow = 'none'
                        }, 150)
                      }}
                    >
                      <div className="brandImageWrapper" style={{
                        position: 'relative',
                        overflow: 'hidden',
                        background: 'var(--color-accent-gray)',
                        aspectRatio: '1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0rem'
                      }}>
                        {marca.logo_url ? (
                          <img
                            className="brandImage"
                            src={marca.logo_url}
                            alt={marca.nombre}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                              transition: 'transform 0.3s ease'
                            }}
                          />
                        ) : (
                          <p style={{
                            fontFamily: 'var(--font-display)',
                            fontWeight: 'var(--font-weight-bold)',
                            fontSize: 'var(--font-size-h3)',
                            color: 'var(--color-text-primary)',
                            textAlign: 'center',
                            margin: 0
                          }}>
                            {marca.nombre}
                          </p>
                        )}
                      </div>
                      <div className="brandInfo" style={{ padding: '1rem' }}>
                        <h3 className="brandName" style={{
                          fontFamily: 'var(--font-ui)',
                          fontSize: 'var(--font-size-body)',
                          fontWeight: 'var(--font-weight-medium)',
                          margin: '0 0 0.25rem 0',
                          color: 'var(--color-text-primary)'
                        }}>{marca.nombre}</h3>
                        {marca.nro_local && (
                          <p style={{
                            fontSize: 'var(--font-size-small)',
                            color: 'var(--color-text-muted)',
                            margin: 0
                          }}>Local {marca.nro_local}</p>
                        )}
                      </div>
                    </Link>
                  )) : (
                    // Placeholder cards while loading
                    [...Array(4)].map((_, i) => (
                      <div key={i} style={{
                        borderRadius: 'var(--radius-lg)',
                        overflow: 'hidden',
                        background: 'white'
                      }}>
                        <div style={{
                          background: 'var(--color-accent-gray)',
                          aspectRatio: '1'
                        }} />
                        <div style={{ padding: '1rem' }}>
                          <div style={{
                            height: '20px',
                            background: 'var(--color-accent-gray)',
                            borderRadius: 'var(--radius-sm)',
                            marginBottom: '0.5rem'
                          }} />
                          <div style={{
                            height: '16px',
                            width: '60%',
                            background: 'var(--color-accent-gray)',
                            borderRadius: 'var(--radius-sm)'
                          }} />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Navigation Buttons - Only show if there are more brands than visible slots */}
              {filteredMarcas.length > brandsToShow && (
                <>
                  <button
                    onClick={prevSlide}
                    aria-label="Previous brands"
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '-1rem',
                      transform: 'translateY(-50%)',
                      background: 'white',
                      border: '1px solid var(--color-stroke)',
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      color: 'var(--color-text-primary)',
                      boxShadow: 'var(--shadow-soft)',
                      transition: 'all 0.2s ease',
                      zIndex: 10
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                      e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'
                      e.currentTarget.style.borderColor = 'var(--brand-blue)'
                      e.currentTarget.style.background = 'var(--brand-blue-50)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = 'var(--shadow-soft)'
                      e.currentTarget.style.transform = 'translateY(-50%)'
                      e.currentTarget.style.borderColor = 'var(--color-stroke)'
                      e.currentTarget.style.background = 'white'
                    }}
                    onMouseDown={(e) => {
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-400)'
                      e.currentTarget.style.background = 'var(--brand-blue-100)'
                    }}
                    onTouchStart={(e) => {
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                      e.currentTarget.style.transform = 'translateY(-50%) scale(0.95)'
                      e.currentTarget.style.background = 'var(--brand-blue-50)'
                    }}
                    onTouchEnd={(e) => {
                      setTimeout(() => {
                        e.currentTarget.style.boxShadow = 'var(--shadow-soft)'
                        e.currentTarget.style.transform = 'translateY(-50%)'
                        e.currentTarget.style.background = 'white'
                      }, 150)
                    }}
                  >
                    ‹
                  </button>

                  <button
                    onClick={nextSlide}
                    aria-label="Next brands"
                    style={{
                      position: 'absolute',
                      top: '50%',
                      right: '-1rem',
                      transform: 'translateY(-50%)',
                      background: 'white',
                      border: '1px solid var(--color-stroke)',
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      color: 'var(--color-text-primary)',
                      boxShadow: 'var(--shadow-soft)',
                      transition: 'all 0.2s ease',
                      zIndex: 10
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                      e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'
                      e.currentTarget.style.borderColor = 'var(--brand-blue)'
                      e.currentTarget.style.background = 'var(--brand-blue-50)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = 'var(--shadow-soft)'
                      e.currentTarget.style.transform = 'translateY(-50%)'
                      e.currentTarget.style.borderColor = 'var(--color-stroke)'
                      e.currentTarget.style.background = 'white'
                    }}
                    onMouseDown={(e) => {
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-400)'
                      e.currentTarget.style.background = 'var(--brand-blue-100)'
                    }}
                    onTouchStart={(e) => {
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                      e.currentTarget.style.transform = 'translateY(-50%) scale(0.95)'
                      e.currentTarget.style.background = 'var(--brand-blue-50)'
                    }}
                    onTouchEnd={(e) => {
                      setTimeout(() => {
                        e.currentTarget.style.boxShadow = 'var(--shadow-soft)'
                        e.currentTarget.style.transform = 'translateY(-50%)'
                        e.currentTarget.style.background = 'white'
                      }, 150)
                    }}
                  >
                    ›
                  </button>
                </>
              )}
            </div>
          </section>
          )}

          {/* Clothing Collection */}
          <section className="clothing-collection" style={{ marginTop: '2rem', marginBottom: '3rem' }}>
            <div className="collection-block" style={{
              background: 'var(--color-black-block)',
              borderRadius: 'var(--radius-lg)',
              overflow: 'hidden',
              display: 'grid',
              gridTemplateColumns: '0.6fr 1fr',
              alignItems: 'center',
              minHeight: '500px'
            }}>
              <div className="collection-image" style={{ height: '100%' }}>
                {homeBlock?.imagen_url ? (
                  <img 
                    src={homeBlock.imagen_url} 
                    alt={homeBlock?.alt_text || 'Tu marca en Plaza Aurora'} 
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: 'var(--radius-md)'
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      background: 'var(--color-black-block)',
                      borderRadius: 'var(--radius-md)'
                    }}
                  />
                )}
              </div>
              <div className="collection-content" style={{
                padding: '3rem',
                color: 'white'
              }}>
                <h2 className="collection-block-title" style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--font-size-display-l)',
                  fontWeight: 'var(--font-weight-bold)',
                  lineHeight: '1.1',
                  margin: '0 0 1.5rem 0',
                  color: 'white'
                }}>
                  {homeBlock?.titulo ? homeBlock.titulo.split('\n').map((line, i) => (
                    <span key={i}>{line}{i < homeBlock.titulo.split('\n').length - 1 && <br />}</span>
                  )) : (
                    <>TU MARCA<br/>EN PLAZA AURORA</>
                  )}
                </h2>
                <p className="collection-block-description" style={{
                  fontSize: 'var(--font-size-lead)',
                  lineHeight: 'var(--line-height-relaxed)',
                  margin: '0 0 2rem 0',
                  color: 'rgba(255, 255, 255, 0.8)',
                  maxWidth: '50ch'
                }}>
                  {homeBlock?.descripcion || 'Únete a las marcas más importantes del país. Espacio estratégico, alto tráfico y la mejor ubicación en Lima Norte.'}
                </p>
                {homeBlock?.boton_activo && homeBlock?.boton_texto && homeBlock?.boton_url ? (
                  homeBlock.boton_url.startsWith('/') ? (
                    <Link to={homeBlock.boton_url} className="btnPrimary" style={{
                      padding: '0.6rem 1.1rem',
                      background: 'white',
                      color: 'var(--color-text-primary)',
                      borderRadius: 'var(--radius-sm)',
                      fontWeight: 'var(--font-weight-bold)',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      fontSize: 'var(--font-size-body)',
                      textDecoration: 'none',
                      display: 'inline-block'
                    }}>{homeBlock.boton_texto}</Link>
                  ) : (
                    <a href={homeBlock.boton_url} target="_blank" rel="noopener noreferrer" className="btnPrimary" style={{
                      padding: '0.6rem 1.1rem',
                      background: 'white',
                      color: 'var(--color-text-primary)',
                      borderRadius: 'var(--radius-sm)',
                      fontWeight: 'var(--font-weight-bold)',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      fontSize: 'var(--font-size-body)',
                      textDecoration: 'none',
                      display: 'inline-block'
                    }}>{homeBlock.boton_texto}</a>
                  )
                ) : (
                  <Link to="/contacto" className="btnPrimary" style={{
                    padding: '0.6rem 1.1rem',
                    background: 'white',
                    color: 'var(--color-text-primary)',
                    borderRadius: 'var(--radius-sm)',
                    fontWeight: 'var(--font-weight-bold)',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    fontSize: 'var(--font-size-body)',
                    textDecoration: 'none',
                    display: 'inline-block'
                  }}>Contáctanos</Link>
                )}
              </div>
            </div>
          </section>

          {/* FAQs Section */}
          {faqs.length > 0 && (
            <section id="faqs" role="region" aria-label="Preguntas Frecuentes" style={{ marginTop: '3rem', marginBottom: '3rem' }}>
              <h2 style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--font-size-display-l)',
                fontWeight: 'var(--font-weight-bold)',
                color: 'var(--color-text-primary)',
                textAlign: 'center',
                marginBottom: '2rem'
              }}>PREGUNTAS FRECUENTES</h2>
              
              <div style={{
                maxWidth: '800px',
                margin: '0 auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem'
              }}>
                {faqs.map((faq) => (
                  <details key={faq.id} style={{
                    background: 'var(--color-surface)',
                    borderRadius: 'var(--radius-lg)',
                    padding: '1.5rem',
                    border: '1px solid var(--color-stroke)',
                    cursor: 'pointer'
                  }}>
                    <summary style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-h4)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--color-text-primary)',
                      listStyle: 'none',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      {faq.pregunta}
                      <span style={{ fontSize: '1.5rem', marginLeft: '1rem' }}>+</span>
                    </summary>
                    <p style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-body)',
                      color: 'var(--color-text-muted)',
                      lineHeight: '1.6',
                      marginTop: '1rem',
                      whiteSpace: 'pre-line'
                    }}>
                      {faq.respuesta}
                    </p>
                  </details>
                ))}
              </div>
              
              {/* FAQPage JSON-LD */}
              <script type="application/ld+json">
                {JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  "mainEntity": faqs.map(faq => ({
                    "@type": "Question",
                    "name": faq.pregunta,
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": faq.respuesta
                    }
                  }))
                })}
              </script>
            </section>
          )}
        </div>
      </main>

      <style>{`
        @keyframes scroll-left {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-100%);
          }
        }

        .featured-card:hover img,
        .brandCard:hover .brandImage {
          transform: scale(1.05);
        }

        .brandCard:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-card);
        }

        .btnPrimary:hover,
        .btnOutline:hover,
        .filter:hover {
          transform: translateY(-2px);
        }

        .btnOutline:hover {
          background-color: var(--color-accent-gray);
        }

        /* Hover sobre tarjetas configurables - botones se vuelven azules */
        .featured-card:hover .featured-info {
          background: var(--brand-blue) !important;
          color: white !important;
        }

        .featured-card:hover .featured-info h3 {
          color: white !important;
        }

        /* Responsive */
        @media (min-width: 600px) and (max-width: 1023px) {
          .heroContainer,
          .collection-header-wrapper,
          .collection-block {
            grid-template-columns: 1fr !important;
          }

          .featured-grid-top {
            grid-template-columns: 1fr 1fr !important;
          }

          .productGrid {
            grid-template-columns: repeat(2, 1fr) !important;
          }

          .brandsSliderTrack {
            grid-template-columns: repeat(${filteredMarcas.length}, calc(50% - 8px)) !important;
          }
        }

        @media (max-width: 599px) {
          .heroContainer,
          .featured-grid-top,
          .collection-header-wrapper,
          .productGrid,
          .collection-block {
            grid-template-columns: 1fr !important;
          }

          .heroTitle {
            font-size: 2.2rem !important;
          }

          .heroImage {
            height: 240px !important;
          }

          .collection-content {
            padding: 2rem 1.5rem !important;
          }
        }
      `}</style>
    </>
  )
}
